// Citation formatting utilities using citation-js
import { Cite } from "@citation-js/core";
import "@citation-js/plugin-csl";
import { Paper, Citation, CitationStyle } from "../types/paper";

// Map our CitationStyle to CSL style templates
const STYLE_MAP: Record<CitationStyle, string> = {
  APA: "apa",
  MLA: "mla",
  Chicago: "chicago-note-bibliography",
  Harvard: "harvard-cite-them-right",
  IEEE: "ieee",
  "UCL-IOE": "ucl-ioe-harvard", // Will use custom formatting
};

/**
 * Format UCL IoE Harvard in-text citation
 * Format: (Author, Year) or (Author & Author, Year) or (Author et al., Year)
 */
function formatUCLIoeInText(paper: Paper): string {
  const year = paper.year;
  const authors = paper.authors;
  
  if (authors.length === 0) {
    return `(Unknown, ${year})`;
  } else if (authors.length === 1) {
    const lastName = authors[0].split(",")[0].trim() || authors[0].split(" ").pop()?.trim() || authors[0];
    return `(${lastName}, ${year})`;
  } else if (authors.length === 2) {
    const lastName1 = authors[0].split(",")[0].trim() || authors[0].split(" ").pop()?.trim() || authors[0];
    const lastName2 = authors[1].split(",")[0].trim() || authors[1].split(" ").pop()?.trim() || authors[1];
    return `(${lastName1} & ${lastName2}, ${year})`;
  } else if (authors.length === 3) {
    const lastName1 = authors[0].split(",")[0].trim() || authors[0].split(" ").pop()?.trim() || authors[0];
    const lastName2 = authors[1].split(",")[0].trim() || authors[1].split(" ").pop()?.trim() || authors[1];
    const lastName3 = authors[2].split(",")[0].trim() || authors[2].split(" ").pop()?.trim() || authors[2];
    return `(${lastName1}, ${lastName2} & ${lastName3}, ${year})`;
  } else {
    // 4+ authors: use et al.
    const lastName1 = authors[0].split(",")[0].trim() || authors[0].split(" ").pop()?.trim() || authors[0];
    return `(${lastName1} et al., ${year})`;
  }
}

/**
 * Format UCL IoE Harvard reference/bibliography entry
 * Journal article format: Author (Year). 'Title'. Journal, Volume(Issue), pp.pages.
 */
function formatUCLIoeReference(paper: Paper): string {
  const year = paper.year;
  const title = paper.title;
  const journal = paper.journal || "Unknown Journal";
  
  // Format authors with initials
  let authorsString = "";
  if (paper.authors.length === 0) {
    authorsString = "Unknown";
  } else {
    const formattedAuthors = paper.authors.map((author) => {
      let lastName = "";
      let initials = "";
      
      if (author.includes(",")) {
        // Format: "LastName, FirstName"
        const parts = author.split(",").map(s => s.trim());
        lastName = parts[0];
        const firstName = parts[1] || "";
        initials = firstName.split(/\s+/).map(n => n.charAt(0).toUpperCase() + ".").join("");
      } else {
        // Format: "FirstName LastName"
        const nameParts = author.trim().split(/\s+/);
        if (nameParts.length >= 2) {
          lastName = nameParts[nameParts.length - 1];
          const firstNames = nameParts.slice(0, -1);
          initials = firstNames.map(n => n.charAt(0).toUpperCase() + ".").join("");
        } else {
          lastName = author;
          initials = "";
        }
      }
      
      if (initials) {
        return `${lastName}, ${initials}`;
      } else {
        return lastName;
      }
    });
    
    // Join authors with "and" before the last one
    if (formattedAuthors.length === 1) {
      authorsString = formattedAuthors[0];
    } else if (formattedAuthors.length === 2) {
      authorsString = formattedAuthors.join(" and ");
    } else {
      const lastAuthor = formattedAuthors[formattedAuthors.length - 1];
      const otherAuthors = formattedAuthors.slice(0, -1).join(", ");
      authorsString = `${otherAuthors} and ${lastAuthor}`;
    }
  }
  
  // Build the reference
  let reference = `${authorsString} (${year}). '${title}'.`;
  
  // Add journal information
  reference += ` ${journal}`;
  
  if (paper.volume) {
    reference += `, ${paper.volume}`;
    if (paper.issue) {
      reference += `(${paper.issue})`;
    }
  }
  
  if (paper.pages) {
    reference += `, pp.${paper.pages}`;
  }
  
  reference += ".";
  
  // Add URL if available
  if (paper.url) {
    reference += ` Available [Online] at: ${paper.url}`;
    if (paper.dateAccessed) {
      reference += ` [Last accessed ${paper.dateAccessed}]`;
    }
    reference += ".";
  }
  
  return reference;
}

/**
 * Convert Paper object to CSL-JSON format for citation-js
 */
function paperToCSL(paper: Paper): any {
  // Use the paper's type if specified, otherwise infer it
  let documentType = paper.type || "article-journal";
  
  // Fallback inference if type not specified (for backward compatibility)
  if (!paper.type) {
    if (paper.url && !paper.journal) {
      documentType = "webpage";
    } else if (paper.url && !paper.doi && !paper.volume && !paper.issue) {
      documentType = "webpage";
    }
  }
  
  const cslData: any = {
    id: paper.id,
    type: documentType,
    title: paper.title,
    author: paper.authors.map((author) => {
      // Try to parse "LastName, FirstName" format
      if (author.includes(",")) {
        const parts = author.split(",").map((s) => s.trim());
        return {
          family: parts[0],
          given: parts[1],
        };
      }
      
      // Handle "FirstName LastName" format (space-separated)
      const nameParts = author.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        // Last word is family name, everything else is given name
        const family = nameParts[nameParts.length - 1];
        const given = nameParts.slice(0, -1).join(" ");
        return {
          family: family,
          given: given,
        };
      }
      
      // Single name - treat as family name only
      return {
        family: author.trim(),
      };
    }),
    issued: {
      "date-parts": [[paper.year]],
    },
    "container-title": paper.journal || paper.bookTitle || undefined,
    volume: paper.volume || undefined,
    issue: paper.issue || undefined,
    page: paper.pages || undefined,
    DOI: paper.doi || undefined,
    URL: paper.url || undefined,
    abstract: paper.abstract || undefined,
    publisher: paper.publisher || undefined,
    edition: paper.edition || undefined,
  };
  
  // Add accessed date - use provided date or current date
  if (paper.url) {
    try {
      let accessDate: Date;
      if (paper.dateAccessed) {
        accessDate = new Date(paper.dateAccessed);
      } else {
        // Use current date if no access date provided
        accessDate = new Date();
      }
      cslData.accessed = {
        "date-parts": [[accessDate.getFullYear(), accessDate.getMonth() + 1, accessDate.getDate()]]
      };
    } catch (error) {
      // Fallback to current date
      const now = new Date();
      cslData.accessed = {
        "date-parts": [[now.getFullYear(), now.getMonth() + 1, now.getDate()]]
      };
    }
  }
  
  return cslData;
}

/**
 * Format a paper as an in-text citation using citation-js
 * Examples: 
 * - APA: (Smith, 2023) or (Smith et al., 2023)
 * - MLA: (Smith) or (Smith et al.)
 * - Chicago: (Smith 2023)
 * - UCL-IOE: (Smith & Jones, 2023) or (Smith et al., 2023)
 */
export function formatInTextCitation(paper: Paper, style: CitationStyle = "APA"): string {
  // Use custom formatting for UCL IoE Harvard
  if (style === "UCL-IOE") {
    return formatUCLIoeInText(paper);
  }
  
  try {
    const cslData = paperToCSL(paper);
    const cite = new Cite(cslData);
    
    // Get citation format
    const styleTemplate = STYLE_MAP[style] || STYLE_MAP.APA;
    
    // Format as citation (in-text style)
    // Note: citation-js generates full bibliography by default
    // For in-text citations, we'll use a simplified format
    const formatted = cite.format("citation", {
      format: "text",
      template: styleTemplate,
      lang: "en-US",
    });
    
    return formatted.trim();
  } catch (error) {
    console.error("Error formatting in-text citation:", error);
    // Fallback to simple format
    const firstAuthor = paper.authors[0]?.split(",")[0] || "Unknown";
    return `(${firstAuthor}, ${paper.year})`;
  }
}

/**
 * Format a paper as a full reference using citation-js
 * Example: 
 * - APA: Smith, J., Johnson, E., & Williams, R. (2023). Deep Learning for NLP. Journal of ML Research.
 * - MLA: Smith, John, Emily Johnson, and Robert Williams. "Deep Learning for NLP." Journal of ML Research (2023).
 * - Chicago: Smith, John, Emily Johnson, and Robert Williams. "Deep Learning for NLP." Journal of ML Research (2023).
 * - UCL-IOE: Smith, J., Johnson, E. and Williams, R. (2023). 'Deep Learning for NLP'. Journal of ML Research, 5(2), pp.10-20.
 */
export function formatReference(paper: Paper, style: CitationStyle = "APA"): string {
  // Use custom formatting for UCL IoE Harvard
  if (style === "UCL-IOE") {
    return formatUCLIoeReference(paper);
  }
  
  try {
    const cslData = paperToCSL(paper);
    const cite = new Cite(cslData);
    
    // Get style template
    const styleTemplate = STYLE_MAP[style] || STYLE_MAP.APA;
    
    // Format as bibliography entry
    const formatted = cite.format("bibliography", {
      format: "text",
      template: styleTemplate,
      lang: "en-US",
    });
    
    return formatted.trim();
  } catch (error) {
    console.error("Error formatting reference:", error);
    // Fallback to simple format
    const authors = paper.authors.join(", ") || "Unknown";
    return `${authors} (${paper.year}). ${paper.title}.`;
  }
}
